/*
 * The phone-bridge control API (ph-logs' /bridge/*).
 *
 * WHAT IT IS: the local ws-scrcpy fork now runs TWO capture engines side by side. The modern one
 * (scrcpy-server v4.1) is required on Android 14+, where the 2021 engine streams black frames
 * because the hidden capture API it used was removed. The legacy engine is deliberately kept and
 * is one click away for testing.
 *
 * WHY IT IS NOT api.js: this talks to the phone bridge on the CUSTOMER'S OWN PC (localhost:6847),
 * not to the Spring server. That means it only works in LOCAL transport - when the browser is on
 * the same machine as the phones. In REMOTE transport the bridge sits behind BM and is not
 * reachable from here, so callers must check isBridgeReachable() and hide the control rather
 * than offer a button that cannot work.
 */

import { WSSCRCPY_BASE, MONITOR_TRANSPORT } from './scrcpyStream';

/** http(s) origin of the bridge, derived from the ws origin the streams already use. */
export const BRIDGE_HTTP_BASE = String(WSSCRCPY_BASE || 'ws://localhost:6847')
  .replace(/^ws:/, 'http:')
  .replace(/^wss:/, 'https:')
  .replace(/\/+$/, '');

/*
 * The Monitor's route is chosen at RUN time (lib/monitorConfig.js): the build constant
 * MONITOR_TRANSPORT is always 'local' in a production bundle. The page reports what it resolved
 * here, so the engine picker keeps appearing only when the page streams direct from this PC.
 * Until the page has resolved (or on any other page) the build value applies, as before.
 */
let resolvedRoute = null;

/** Called by the Monitor page with its resolved page route: 'local' (direct) | 'remote' | null. */
export function setResolvedTransport(route) {
  resolvedRoute = route === 'local' || route === 'remote' ? route : null;
}

/** Only meaningful when the browser can actually reach the customer's own bridge. */
export const bridgeIsLocal = () => (resolvedRoute || MONITOR_TRANSPORT) !== 'remote';

async function call(path, options) {
  const res = await fetch(BRIDGE_HTTP_BASE + path, options);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || ('HTTP ' + res.status));
  return body;
}

/** {bridge, engine, minSdk, overrides:{udid:mode}, sessions:[...]} or null if unreachable. */
export async function getBridgeHealth() {
  if (!bridgeIsLocal()) return null;
  try {
    return await call('/bridge/health', { method: 'GET' });
  } catch (e) {
    return null;   // bridge absent or an older ph-logs: the picker simply does not appear
  }
}

/** mode: 'v3' (modern) | 'legacy' (2021 engine) | 'auto' (decide by Android version). */
export async function setDeviceEngine(udid, mode) {
  return call('/bridge/route', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ udid, mode }),
  });
}

export const ENGINE_MODES = [
  { value: 'auto', label: 'Auto', hint: 'Modern engine on Android 14+, legacy below it' },
  { value: 'v3', label: 'Modern', hint: 'scrcpy v4.1 - required on Android 14+' },
  { value: 'legacy', label: 'Legacy', hint: 'The 2021 engine - kept for testing; black screen on Android 14+' },
];
